import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Circle, Download, FileSignature, KeyRound,
  LoaderCircle, LogOut, PartyPopper, ShieldCheck, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { compressImageFile } from "@/lib/imageCompression";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;
const TOKEN_KEY = "juke_onboarding_token";

const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ");
  return fallback;
};

const FIELD = "w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 outline-none transition-colors";
const GOLD_BTN = "w-full h-11 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-sm transition-colors";

export default function DriverOnboarding() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) { setChecking(false); return; }
    try {
      const { data } = await client.get("/me");
      setMe(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setMe(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setMe(null); };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">Juke Transfers</p>
          <div className="flex items-center gap-4">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Portal za uvođenje</p>
            {me && (
              <button data-testid="onboarding-logout" onClick={logout}
                className="text-zinc-500 hover:text-white transition-colors" aria-label="Odjava">
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {checking ? (
          <div className="flex justify-center py-20"><LoaderCircle className="w-6 h-6 animate-spin text-[#C5A059]" /></div>
        ) : !me ? (
          <LoginCard onSuccess={loadMe} />
        ) : me.must_change_password ? (
          <ChangePasswordCard onDone={loadMe} />
        ) : (
          <Checklist me={me} refresh={loadMe} />
        )}
      </main>
    </div>
  );
}

function LoginCard({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      toast.error(errMsg(err, "Prijava nije uspjela."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="onboarding-login-form"
      className="max-w-md mx-auto rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 space-y-5">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-full border border-[#C5A059]/40 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-[#C5A059]" />
        </div>
        <h1 className="text-2xl text-white font-serif">Dobrodošli u Juke</h1>
        <p className="text-sm text-zinc-500">Prijavite se podacima iz e-maila dobrodošlice.</p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">E-mail</label>
        <input data-testid="onboarding-login-email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} className={FIELD} placeholder="vas@email.com" required />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Lozinka</label>
        <input data-testid="onboarding-login-password" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} className={FIELD} placeholder="Privremena lozinka iz e-maila" required />
      </div>
      <button data-testid="onboarding-login-submit" type="submit" disabled={busy} className={GOLD_BTN}>
        {busy ? "Prijava…" : "Prijavi se"}
      </button>
    </form>
  );
}

function ChangePasswordCard({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Nova lozinka mora imati najmanje 8 znakova.");
    if (next !== confirm) return toast.error("Lozinke se ne podudaraju.");
    setBusy(true);
    try {
      await client.post("/change-password", { current_password: current, new_password: next });
      toast.success("Lozinka je promijenjena.");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Promjena lozinke nije uspjela."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="onboarding-change-password-form"
      className="max-w-md mx-auto rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 space-y-5">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-full border border-[#C5A059]/40 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-[#C5A059]" />
        </div>
        <h1 className="text-2xl text-white font-serif">Postavite novu lozinku</h1>
        <p className="text-sm text-zinc-500">Iz sigurnosnih razloga zamijenite privremenu lozinku vlastitom.</p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Trenutna (privremena) lozinka</label>
        <input data-testid="onboarding-current-password" type="password" value={current}
          onChange={(e) => setCurrent(e.target.value)} className={FIELD} required />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Nova lozinka (min. 8 znakova)</label>
        <input data-testid="onboarding-new-password" type="password" value={next}
          onChange={(e) => setNext(e.target.value)} className={FIELD} required />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Ponovite novu lozinku</label>
        <input data-testid="onboarding-confirm-password" type="password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} className={FIELD} required />
      </div>
      <button data-testid="onboarding-change-password-submit" type="submit" disabled={busy} className={GOLD_BTN}>
        {busy ? "Spremanje…" : "Spremi novu lozinku"}
      </button>
    </form>
  );
}

function Checklist({ me, refresh }) {
  const c = me.checklist;
  const totalSteps = 1 + (c.contracts.length ? 1 : 0) + (c.needs_tax_office ? 1 : 0);
  const doneSteps = (c.password_changed ? 1 : 0) + (c.contracts_done && c.contracts.length ? 1 : 0) + (c.needs_tax_office && c.tax_done ? 1 : 0);

  return (
    <div className="space-y-8" data-testid="onboarding-checklist">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">Uvođenje</p>
        <h1 className="text-3xl sm:text-4xl text-white font-serif">Pozdrav, {me.first_name}</h1>
        <p className="text-sm text-zinc-500 mt-2">
          {c.complete
            ? "Sve je dovršeno — dobrodošli u Juke flotu."
            : "Dovršite korake u nastavku kako biste aktivirali svoj račun."}
        </p>
        <div className="mt-5 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-[#C5A059] transition-all duration-700"
            style={{ width: `${Math.round((doneSteps / totalSteps) * 100)}%` }} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-2">
          {doneSteps} / {totalSteps} koraka dovršeno
        </p>
      </div>

      {c.complete && (
        <div data-testid="onboarding-complete-banner"
          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 flex items-start gap-4">
          <PartyPopper className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium">Uvođenje dovršeno — račun je aktivan</p>
            <p className="text-sm text-zinc-400 mt-1">
              Administrator je obaviješten. Uskoro ćete primiti podatke za vozačku aplikaciju i prve vožnje. Sretan put!
            </p>
          </div>
        </div>
      )}

      <StepCard
        icon={KeyRound}
        done={c.password_changed}
        title="Lozinka postavljena"
        subtitle="Privremena lozinka zamijenjena je vašom vlastitom."
      />

      <div className={`rounded-2xl border ${c.contracts_done && c.tax_done ? "border-emerald-500/30" : "border-white/10"} bg-[#0d0d0d] p-6 space-y-4`}>
        <div className="flex items-center gap-3">
          {c.contracts_done && c.tax_done
            ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            : <FileSignature className="w-5 h-5 text-[#C5A059] shrink-0" />}
          <div>
            <p className="text-white font-medium">Pregled i potpis ugovora</p>
            <p className="text-xs text-zinc-500 mt-0.5">Svaki dokument ima vlastiti postupak — potpis, potvrda ili ovjera u Poreznoj upravi.</p>
          </div>
        </div>
        <div className="space-y-3">
          {c.contracts.map((ct) => (
            <ContractRow key={ct.slot} contract={ct} checklist={c} refresh={refresh} />
          ))}
          {c.contracts.length === 0 && (
            <p className="text-sm text-zinc-500">Ugovori se pripremaju — provjerite ponovno uskoro.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StepCard({ icon: Icon, done, title, subtitle }) {
  return (
    <div className={`rounded-2xl border ${done ? "border-emerald-500/30" : "border-white/10"} bg-[#0d0d0d] p-6 flex items-center gap-3`}>
      {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Icon className="w-5 h-5 text-[#C5A059] shrink-0" />}
      <div>
        <p className="text-white font-medium">{title}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

const PDF_NAMES = {
  contract_employment: "Ugovor_o_radu.pdf",
  contract_lease: "Ugovor_o_najmu_vozila.pdf",
  contract_internal: "Interni_akt.pdf",
  contract_handover: "Zapisnik_o_zaduzenju_vozila.pdf",
  contract_partner: "Ugovor_o_suradnji.pdf",
  contract_lease_v2: "Ugovor_o_zakupu_vozila.pdf",
};

function ContractRow({ contract, checklist, refresh }) {
  const requires = contract.requires || "signature";
  const [signing, setSigning] = useState(false);
  const [fullName, setFullName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewBusy, setViewBusy] = useState(false);
  const done = contract.done ?? contract.signed;

  const viewPdf = async () => {
    if (viewBusy) return;
    setViewBusy(true);
    try {
      const resp = await client.get(`/contracts/${contract.slot}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = PDF_NAMES[contract.slot] || `${contract.slot}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(errMsg(err, "Dokument nije moguće otvoriti."));
    } finally {
      setViewBusy(false);
    }
  };

  const sign = async () => {
    if (fullName.trim().length < 3) return toast.error("Upišite svoje puno ime i prezime.");
    if (!consent) return toast.error("Označite suglasnost s uvjetima ugovora.");
    setBusy(true);
    try {
      const { data } = await client.post(`/contracts/${contract.slot}/sign`, {
        full_name: fullName.trim(), consent: true,
      });
      toast.success(data.onboarding_closed
        ? "Ugovor potpisan — uvođenje dovršeno!"
        : "Ugovor je potpisan — potpis je trajno upisan u PDF dokument.");
      setSigning(false);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Potpisivanje nije uspjelo."));
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (checked) => {
    if (!checked || busy) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/contracts/${contract.slot}/acknowledge`, { consent: true });
      toast.success(data.onboarding_closed
        ? "Interni akt prihvaćen — uvođenje dovršeno!"
        : "Interni akt je prihvaćen.");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Potvrda nije uspjela."));
    } finally {
      setBusy(false);
    }
  };

  const uploadCertified = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const prepared = await compressImageFile(file);
      const form = new FormData();
      form.append("file", prepared);
      const { data } = await client.post("/certified-copy", form, { timeout: 120_000 });
      toast.success(data.onboarding_closed
        ? "Ovjereni primjerak zaprimljen — uvođenje dovršeno!"
        : "Ovjereni primjerak je uspješno učitan.");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Učitavanje nije uspjelo. Pokušajte ponovno."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`contract-row-${contract.slot}`}
      className="rounded-xl border border-white/10 bg-[#050505] p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {done
            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            : <Circle className="w-4 h-4 text-zinc-600 shrink-0" />}
          <p className="text-sm text-white truncate">{contract.label}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`contract-view-${contract.slot}`} onClick={viewPdf} disabled={viewBusy}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] disabled:opacity-60 transition-colors">
            {viewBusy ? <LoaderCircle className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Pregledaj
          </button>
          {requires === "signature" && !contract.signed && (
            <button data-testid={`contract-sign-open-${contract.slot}`} onClick={() => setSigning((s) => !s)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-colors">
              <FileSignature className="w-3 h-3" /> Potpiši
            </button>
          )}
        </div>
      </div>

      {requires === "signature" && !contract.signed && signing && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-3" data-testid={`contract-sign-form-${contract.slot}`}>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Puno ime i prezime"
            className="w-full h-11 bg-black/40 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none focus:border-[#C5A059]/60 transition-colors"
            data-testid={`contract-sign-name-${contract.slot}`}
          />
          {fullName.trim().length >= 3 && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pregled potpisa</p>
              <p className="text-2xl text-[#C5A059] italic" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                {fullName.trim()}
              </p>
            </div>
          )}
          <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#C5A059] shrink-0"
              data-testid={`contract-sign-consent-${contract.slot}`}
            />
            Pročitao/la sam dokument i suglasan/na sam s uvjetima — ovaj digitalni potpis trajno se upisuje u PDF.
          </label>
          <button
            onClick={sign} disabled={busy}
            className="w-full h-11 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] font-semibold uppercase tracking-[0.2em] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
            data-testid={`contract-sign-submit-${contract.slot}`}
          >
            {busy ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
            {busy ? "Potpisivanje…" : "Potpiši dokument"}
          </button>
        </div>
      )}

      {requires === "signature" && contract.signed && (
        <p className="text-[11px] text-zinc-500 mt-2">
          Potpisano: {contract.signed_name} · {contract.signed_at ? new Date(contract.signed_at).toLocaleString("hr-HR") : ""}
        </p>
      )}

      {requires === "acknowledge" && (
        done ? (
          <p className="text-[11px] text-zinc-500 mt-2" data-testid={`contract-ack-done-${contract.slot}`}>
            Prihvaćeno {contract.signed_at ? new Date(contract.signed_at).toLocaleString("hr-HR") : ""}
          </p>
        ) : (
          <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer mt-3 pt-3 border-t border-white/10">
            <input data-testid={`contract-ack-checkbox-${contract.slot}`} type="checkbox"
              checked={false} disabled={busy}
              onChange={(e) => acknowledge(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#C5A059]" />
            {busy ? "Spremanje…" : "Pročitao/la sam i prihvaćam interni akt."}
          </label>
        )
      )}

      {requires === "upload" && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
          <div data-testid={`lease-instructions-${contract.slot}`}
            className="rounded-lg border border-[#C5A059]/30 bg-[#C5A059]/5 p-3">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">
              Ovjera u Poreznoj upravi — OBAVEZNO
            </p>
            <ul className="text-xs text-zinc-300 mt-2 space-y-1 list-disc list-inside leading-relaxed">
              <li>Porezna uprava Split, 2. kat, soba 10</li>
              <li>Ponesite potpisani Ugovor o zakupu vozila iz svog portala</li>
              <li>Ovjerit će ga na licu mjesta</li>
              <li>Skenirajte ovjerenu stranicu i učitajte u aplikaciju — uvođenje se zatvara tek kada zaprimimo ovjereni primjerak</li>
            </ul>
          </div>
          {checklist?.certified_uploaded ? (
            <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="lease-certified-done">
              <CheckCircle2 className="w-4 h-4" />
              Ovjereni primjerak zaprimljen {checklist.certified_at ? new Date(checklist.certified_at).toLocaleString("hr-HR") : ""}
            </p>
          ) : (
            <label className={`flex items-center justify-center gap-2 h-12 rounded-xl border border-dashed ${busy ? "border-zinc-700 text-zinc-600" : "border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/5 cursor-pointer"} text-sm transition-colors`}>
              {busy ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? "Učitavanje…" : "Učitaj ovjereni primjerak (fotografija ili PDF)"}
              <input data-testid="certified-upload-input" type="file" accept="image/*,application/pdf"
                className="hidden" onChange={uploadCertified} disabled={busy} />
            </label>
          )}
        </div>
      )}
    </div>
  );
}
